import React from 'react';
import { DispensaryPaymentOptions } from 'src/checkout/components/sections/payment/shared/utils';
import styled, { css } from 'styled-components';
import { RadioGroup as _RadioGroup, Radio as _Radio } from 'react-radio-group';
import { useObserver } from 'mobx-react-lite';
import { ApolloError } from 'apollo-client';

import useStores from 'shared/hooks/use-stores';
import { ModalButton } from 'shared/modals';
import { Image } from 'shared/components';
import RadioCheckedIcon from 'shared/assets/radio-checked';
import RadioUncheckedIcon from 'shared/assets/radio-unchecked';
import useStyledTheme from 'shared/hooks/use-styled-theme';
import { ErrorContent } from 'src/components/modals/kiosk-checkout/v2/error-content';
import { PAYMENT_OPTION_ICONS } from 'src/payments/constants';
import { Typography } from 'src/typography';

type NonPbbOptionsProps = {
  counterPaymentOptions: DispensaryPaymentOptions;
  onPaymentSelect: (value: string) => void;
  handleUpdateOrderPayment: () => Promise<void>;
  error?: ApolloError;
  handleOnErrorClose: () => void;
};

export const NonPbbOptions = ({
  counterPaymentOptions,
  handleUpdateOrderPayment,
  onPaymentSelect,
  error,
  handleOnErrorClose,
}: NonPbbOptionsProps): JSX.Element => {
  const theme = useStyledTheme();
  const { Cart } = useStores();
  const paymentMethod = useObserver(() => Cart.order.paymentMethod);

  const headingText = counterPaymentOptions.length === 1 ? 'We accept:' : 'Select Payment Types:';

  if (error) {
    return <ErrorContent onClose={handleOnErrorClose} />;
  }

  return (
    <>
      <Container>
        <Heading size='medium'>{headingText}</Heading>
        <RadioGroup
          name='payment-options'
          onChange={onPaymentSelect}
          selectedValue={paymentMethod || counterPaymentOptions[0].value}
          role='radiogroup'
          aria-label='payment-options'
        >
          {counterPaymentOptions.map((option) => {
            const selected = option.value === paymentMethod;
            return (
              <RadioLabel
                key={option.value}
                selected={selected}
                totalItems={counterPaymentOptions.length}
                name='payment-options'
              >
                <RadioLabelContent>
                  <Image src={PAYMENT_OPTION_ICONS[option.value]} alt={option.label} height={24} />
                  <RadioLabelText size='large'>
                    {option.label} {option.sublabel && <span>{option.sublabel}</span>}
                  </RadioLabelText>
                </RadioLabelContent>
                {option.value === paymentMethod && <RadioCheckedIcon color={theme.customized.colors.buttonsLinks} />}
                {option.value !== paymentMethod && <RadioUncheckedIcon />}
                <Radio name='radio' value={option.value} aria-checked={selected} role='radio' />
              </RadioLabel>
            );
          })}
        </RadioGroup>
      </Container>
      <ContinueButton onClick={() => handleUpdateOrderPayment()}>Continue</ContinueButton>
    </>
  );
};

const Container = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    padding: ${spaces[6]};
    background-color: ${colors.white};
    border-radius: 16px;
  `}
`;

const Heading = styled(Typography.Heading)`
  ${({ theme: { spaces } }) => css`
    margin-bottom: ${spaces[6]};
  `}
`;

const RadioGroup = styled(_RadioGroup)`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const RadioLabel = styled.label<{ totalItems: number }>`
  ${({ theme: { colors, spaces }, totalItems }) => css`
    display: flex;
    justify-content: space-between;
    flex: 1;
    flex-basis: ${totalItems === 3 ? 'calc(33.333% - 7px)' : 'calc(50% - 5px)'};
    max-width: calc(50% - 5px);
    background-color: ${colors.grey[95]};
    border-radius: 8px;
    padding: ${spaces[4]};
    border: none;
    cursor: pointer;
  `}
`;

const RadioLabelText = styled(Typography.Body)`
  ${({ theme: { colors } }) => css`
    color: ${colors.gray40};
    font-size: 20px;
  `}
`;

const Radio = styled(_Radio)`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
`;

const RadioLabelContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  font-size: 20px;
`;

const ContinueButton = styled(ModalButton)`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: ${spaces[6]};
    width: 100%;
    text-transform: uppercase;
    height: 64px;
    font-size: 24px;
  `}
`;
